import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import ToastContainer from "../components/ui/ToastContainer";
import { humanizeError } from "../utils/userFriendlyError";

const ToastContext = createContext(null);

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return ctx;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const counterRef = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((opts) => {
    const id = `toast-${Date.now()}-${++counterRef.current}`;
    setToasts((prev) => [
      ...prev,
      {
        id,
        type: opts?.type || "info",
        message: opts?.message || "",
        title: opts?.title,
        duration: opts?.duration,
      },
    ]);
    return id;
  }, []);

  const toast = useMemo(
    () => ({
      success: (message, opts = {}) =>
        push({ ...opts, type: "success", message }),
      error: (message, opts = {}) =>
        push({ ...opts, type: "error", message }),
      info: (message, opts = {}) => push({ ...opts, type: "info", message }),
      warning: (message, opts = {}) =>
        push({ ...opts, type: "warning", message }),
      fromError: (err, fallback, opts = {}) =>
        push({ ...opts, type: "error", message: humanizeError(err, fallback) }),
      dismiss,
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
};

export default ToastProvider;
